import { Barbell, PencilSimple, Trash, Lightning, Copy, DotsSixVertical, FloppyDisk } from "@phosphor-icons/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MuscleVolumeSummary from "./MuscleVolumeSummary";

export default function WorkoutCard({ workout, getPhaseTitle, exerciseLibrary, userMappings, onEdit, onDelete, onCopy, onSaveAsTemplate, sortable = false }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: workout.id, disabled: !sortable });

  const style = sortable ? {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  } : {};

  return (
    <div
      ref={sortable ? setNodeRef : undefined}
      style={style}
      className={`bg-[#171717] border rounded-lg overflow-hidden ${
        isDragging ? "border-[#007AFF]/50 shadow-lg shadow-[#007AFF]/10" : "border-[#262626]"
      }`}
      data-testid={`workout-card-${workout.id}`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E1E1E]">
        <div className="flex items-center gap-2.5">
          {sortable && (
            <button
              {...attributes}
              {...listeners}
              className="text-zinc-600 hover:text-zinc-400 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
              data-testid={`drag-workout-${workout.id}`}
            >
              <DotsSixVertical size={16} weight="bold" />
            </button>
          )}
          <div className="w-7 h-7 rounded-md bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
            <Barbell size={14} weight="fill" className="text-[#007AFF]" />
          </div>
          <div>
            <h4 className="font-semibold text-white text-sm">{workout.title}</h4>
            <div className="flex gap-1.5 mt-0.5">
              {workout.day_of_week && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#007AFF]/10 text-[#007AFF] rounded-full font-medium">{workout.day_of_week}</span>
              )}
              {workout.phase_id && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#D4FF00]/10 text-[#D4FF00] rounded-full font-medium flex items-center gap-0.5">
                  <Lightning size={8} weight="fill" />{getPhaseTitle(workout.phase_id)}
                </span>
              )}
              <span className="text-[10px] text-zinc-600">{workout.exercises?.length || 0} esercizi</span>
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          {onCopy && (
            <button onClick={() => onCopy(workout)} className="w-6 h-6 rounded flex items-center justify-center text-zinc-600 hover:text-[#D4FF00] hover:bg-[#D4FF00]/10 transition-all" data-testid={`copy-workout-${workout.id}`}>
              <Copy size={14} />
            </button>
          )}
          {onSaveAsTemplate && (
            <button
              onClick={() => onSaveAsTemplate(workout)}
              className="w-6 h-6 rounded flex items-center justify-center text-zinc-600 hover:text-[#D4FF00] hover:bg-[#D4FF00]/10 transition-all"
              title="Salva come template"
              data-testid={`save-as-template-${workout.id}`}
            >
              <FloppyDisk size={14} />
            </button>
          )}
          <button onClick={() => onEdit(workout)} className="w-6 h-6 rounded flex items-center justify-center text-zinc-600 hover:text-[#007AFF] hover:bg-[#007AFF]/10 transition-all" data-testid={`edit-workout-${workout.id}`}>
            <PencilSimple size={14} />
          </button>
          <button onClick={() => onDelete(workout.id)} className="w-6 h-6 rounded flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all" data-testid={`delete-workout-${workout.id}`}>
            <Trash size={14} />
          </button>
        </div>
      </div>
      {workout.exercises?.length > 0 && (
        <div className="px-4 py-2.5">
          <div className="grid grid-cols-12 gap-2 px-2 pb-1.5 mb-0.5">
            <span className="col-span-5 text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-600">Esercizio</span>
            <span className="col-span-2 text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-600 text-center">Serie</span>
            <span className="col-span-2 text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-600 text-center">Reps</span>
            <span className="col-span-3 text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-600 text-center">Recupero</span>
          </div>
          {workout.exercises.map((ex, j) => (
            <div key={`${workout.id}-ex-${j}`} className="grid grid-cols-12 gap-2 items-center px-2 py-2 rounded-md hover:bg-white/[0.02] transition-colors">
              <div className="col-span-5 flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-[#262626] flex items-center justify-center text-[9px] font-bold text-zinc-500 flex-shrink-0">{j + 1}</span>
                <span className="text-xs text-white font-medium truncate">{ex.name}</span>
              </div>
              <div className="col-span-2 flex justify-center">
                <span className="px-2 py-0.5 rounded bg-[#007AFF]/10 text-[#007AFF] text-[11px] font-bold tabular-nums">{ex.sets}</span>
              </div>
              <div className="col-span-2 flex justify-center">
                <span className="px-2 py-0.5 rounded bg-[#D4FF00]/10 text-[#D4FF00] text-[11px] font-bold tabular-nums">{ex.reps}</span>
              </div>
              <div className="col-span-3 flex justify-center">
                {ex.rest_seconds ? (
                  <span className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[11px] font-medium tabular-nums">{ex.rest_seconds}s</span>
                ) : <span className="text-zinc-700 text-xs">-</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {workout.exercises?.length > 0 && (
        <MuscleVolumeSummary exercises={workout.exercises} exerciseLibrary={exerciseLibrary} userMappings={userMappings} />
      )}
    </div>
  );
}
